import { api } from './client'

/**
 * R5-10 — cassaforte credenziali di portali e caselle. Solo Admin, attiva con il flag `credenziali.cassaforte`.
 * Elenco e dettaglio non restituiscono mai i segreti: solo se ci sono. Password, codice TOTP e codici ricevuti per mail
 * si leggono con chiamate dedicate, tutte registrate nell'audit (la password chiede anche il motivo).
 */
export type CredentialKind = 'portale' | 'mail' | 'altro'

export const CREDENTIAL_KINDS: { value: CredentialKind; label: string }[] = [
  { value: 'portale', label: 'Portale' },
  { value: 'mail', label: 'Casella mail' },
  { value: 'altro', label: 'Altro' },
]

export interface CredentialImap {
  enabled: boolean
  host: string | null
  port: number
  useSsl: boolean
  username: string | null
  hasPassword: boolean
  folder: string
  senderFilter: string | null
  codeRegex: string | null
  lastPolledAt: string | null
  lastError: string | null
}

export interface Credential {
  id: number
  name: string
  kind: CredentialKind
  url: string | null
  username: string | null
  notes: string | null
  hasPassword: boolean
  hasTotp: boolean
  totpDigits: number
  totpPeriod: number
  totpAlgorithm: string
  totpLabel: string | null
  imap: CredentialImap
  activeCodes: number
  createdAt: string
  updatedAt: string | null
}

export interface CredentialList {
  encryptionAvailable: boolean
  kinds: CredentialKind[]
  items: Credential[]
}

/** Segreti: `null`/assente = invariato, `''` = cancellato, altro = nuovo valore. */
export interface CredentialBody {
  name: string
  kind: CredentialKind
  url?: string | null | undefined
  username?: string | null | undefined
  password?: string | null | undefined
  notes?: string | null | undefined
  /** URI otpauth:// oppure segreto Base32. */
  totp?: string | null | undefined
  imap?: {
    enabled: boolean
    host?: string | null | undefined
    port?: number | null | undefined
    useSsl?: boolean | null | undefined
    username?: string | null | undefined
    password?: string | null | undefined
    folder?: string | null | undefined
    senderFilter?: string | null | undefined
    codeRegex?: string | null | undefined
  } | undefined
}

export interface TotpCodeResponse {
  code: string
  remainingSeconds: number
  period: number
}

export interface ReceivedCode {
  id: number
  code: string
  senderDomain: string | null
  receivedAt: string
  expiresAt: string
}

const base = '/api/settings/credentials'

export const credentialVaultApi = {
  list: () => api<CredentialList>(base),
  get: (id: number) => api<Credential>(`${base}/${id}`),
  create: (body: CredentialBody) => api<Credential>(base, { method: 'POST', body: JSON.stringify(body) }),
  update: (id: number, body: CredentialBody) =>
    api<Credential>(`${base}/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  remove: (id: number) => api<void>(`${base}/${id}`, { method: 'DELETE' }),
  revealPassword: (id: number, reason: string) =>
    api<{ password: string }>(`${base}/${id}/reveal-password`, { method: 'POST', body: JSON.stringify({ reason }) }),
  totpCode: (id: number) => api<TotpCodeResponse>(`${base}/${id}/totp/code`, { method: 'POST' }),
  uploadTotpQr: (id: number, imageBase64: string) =>
    api<Credential>(`${base}/${id}/totp/qr`, { method: 'POST', body: JSON.stringify({ imageBase64 }) }),
  codes: (id: number) => api<ReceivedCode[]>(`${base}/${id}/codes`),
  poll: (id: number) =>
    api<{ captured: number; lastError: string | null; lastPolledAt: string | null }>(`${base}/${id}/imap/poll`, { method: 'POST' }),
}
